import { useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { EditMembershipSchemaUsdcType } from '@components/SpaceSettingsPanel/editMembershipSchemaUsdc'
import { FadeInBox } from '@components/Transitions'
import { ErrorMessage, RadioCard, Stack, TextField } from '@ui'
import { TOWNS_PRICING_TERMS_LINK } from 'data/links'
import { useDevice } from 'hooks/useDevice'
import { atoms } from 'ui/styles/atoms.css'

import { formatDecimalValue } from '../utils'

export function EditUsdcPricing({ isCurrentlyFree }: { isCurrentlyFree: boolean }) {
    const { formState, setValue, watch, trigger } = useFormContext<EditMembershipSchemaUsdcType>()
    const formProps = useFormContext<EditMembershipSchemaUsdcType>()

    const usdcPricingOption = watch('usdcPricingOption')
    const defaultPrice = formState.defaultValues?.membershipCostUsdc ?? ''
    const isTouch = useDevice().isTouch

    const onCostChange = useCallback(
        (value: string) => {
            const formatted = formatDecimalValue(value)
            setValue('membershipCostUsdc', formatted, { shouldValidate: false })
            trigger('membershipCostUsdc')
        },
        [setValue, trigger],
    )

    // When pricing option changes, update the price field accordingly
    useEffect(() => {
        switch (usdcPricingOption) {
            case 'free': {
                setValue('membershipCostUsdc', '0', { shouldValidate: true })
                break
            }
            case 'fixed': {
                // If currently free, start with empty field for user to enter amount
                // If already paid, keep the current price
                const priceToSet = isCurrentlyFree ? '' : defaultPrice
                setValue('membershipCostUsdc', priceToSet, { shouldValidate: !!priceToSet })
                break
            }
        }
    }, [usdcPricingOption, setValue, isCurrentlyFree, defaultPrice])

    return (
        <Stack gap="sm">
            <RadioCard
                name="usdcPricingOption"
                value="free"
                title="Free"
                description={
                    <>
                        Town is free to join.{' '}
                        <a
                            href={TOWNS_PRICING_TERMS_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={atoms({
                                textDecoration: 'underline',
                            })}
                        >
                            Terms
                        </a>{' '}
                        apply.
                    </>
                }
                dataTestId="membership-pricing-type-usdc-free"
                {...formProps}
            />
            <RadioCard
                name="usdcPricingOption"
                value="fixed"
                title="Fixed"
                description="Everyone pays the same price."
                dataTestId="membership-pricing-type-usdc-fixed"
                {...formProps}
            >
                {usdcPricingOption === 'fixed' ? (
                    <TextField
                        autoFocus
                        background="level3"
                        autoComplete="one-time-code"
                        data-testid="fixed-town-membership-cost-usdc"
                        placeholder={isTouch ? '' : 'Enter amount'}
                        {...formProps.register('membershipCostUsdc')}
                        after="USDC"
                        inputMode="decimal"
                        border={
                            formProps.formState.errors['membershipCostUsdc']
                                ? 'negative'
                                : undefined
                        }
                        onChange={(e) => onCostChange(e.target.value)}
                    />
                ) : null}
            </RadioCard>
            {formState.errors['membershipCostUsdc'] ? (
                <FadeInBox key="error">
                    <ErrorMessage errors={formState.errors} fieldName="membershipCostUsdc" />
                </FadeInBox>
            ) : formState.errors['usdcPricingOption'] ? (
                <FadeInBox key="error">
                    <ErrorMessage errors={formState.errors} fieldName="usdcPricingOption" />
                </FadeInBox>
            ) : null}
        </Stack>
    )
}
